import React, { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import DynamicForm from "@/components/common/DynamicForm";
import { Spinner } from "@/components/common/Primitives";
import { getContactEmails } from "@/lib/settings";
import { isInitialLoading, useSettings, useActiveForms } from "@/hooks/useSiteQueries";

export default function ContactPage() {
  const settingsQuery = useSettings();
  const formsQuery = useActiveForms();
  const [activeForm, setActiveForm] = useState(null);

  const forms = formsQuery.data;
  const settings = settingsQuery.data;

  React.useEffect(() => {
    if (forms?.length && !activeForm) setActiveForm(forms[0]);
  }, [forms, activeForm]);

  if (isInitialLoading([settingsQuery, formsQuery])) return <Spinner className="py-32" />;

  const contactEmails = getContactEmails(settings);

  return (
    <div data-testid="contact-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-24">
          <p className="eyebrow !text-gold-light mb-4">Contact Us</p>
          <h1 className="heading-xl !text-white max-w-3xl">
            We'd love to <span className="italic text-gold-light">hear from you.</span>
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            Membership, volunteering, sponsorship, civic issues — reach the Sabha office directly or use a form below.
          </p>
        </div>
      </section>

      <section className="container-bcs py-14 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact details */}
          <div className="lg:col-span-2 space-y-5">
            <a href={`tel:${settings.contact_phone}`} className="card-bcs p-6 flex items-start gap-4 group" data-testid="contact-phone-card">
              <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon shrink-0">
                <Phone size={19} />
              </span>
              <div>
                <p className="font-serif text-lg font-semibold text-ink group-hover:text-maroon transition-colors">Call the office</p>
                <p className="text-sm text-ink-soft mt-0.5">{settings.contact_phone}</p>
              </div>
            </a>
            {contactEmails.map((email, i) => (
              <a
                key={email}
                href={`mailto:${email}`}
                className="card-bcs p-6 flex items-start gap-4 group"
                data-testid={`contact-email-card-${i}`}
              >
                <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon shrink-0">
                  <Mail size={19} />
                </span>
                <div>
                  <p className="font-serif text-lg font-semibold text-ink group-hover:text-maroon transition-colors">
                    {contactEmails.length === 1 ? "Email us" : `Email us${contactEmails.length > 1 ? ` (${i + 1})` : ""}`}
                  </p>
                  <p className="text-sm text-ink-soft mt-0.5">{email}</p>
                </div>
              </a>
            ))}
            <a
              href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="card-bcs p-6 flex items-start gap-4 group"
              data-testid="contact-whatsapp-card"
            >
              <span className="h-11 w-11 rounded-lg bg-green-50 flex items-center justify-center text-green-700 shrink-0">
                <MessageCircle size={19} />
              </span>
              <div>
                <p className="font-serif text-lg font-semibold text-ink group-hover:text-maroon transition-colors">WhatsApp</p>
                <p className="text-sm text-ink-soft mt-0.5">{settings.whatsapp}</p>
              </div>
            </a>
            <div className="card-bcs p-6 flex items-start gap-4">
              <span className="h-11 w-11 rounded-lg bg-gold-50 flex items-center justify-center text-gold-dark shrink-0">
                <MapPin size={19} />
              </span>
              <div>
                <p className="font-serif text-lg font-semibold text-ink">Office address</p>
                <p className="text-sm text-ink-soft mt-0.5 leading-relaxed">{settings.address}</p>
                <p className="text-xs text-ink-muted mt-2 flex items-center gap-1.5">
                  <Clock size={13} /> {settings.office_hours}
                </p>
              </div>
            </div>
          </div>

          {/* Forms */}
          <div className="lg:col-span-3">
            <div className="card-bcs p-7 sm:p-9">
              <p className="eyebrow mb-3">Online Forms</p>
              <h2 className="heading-md">Send us a request</h2>
              <div className="mt-5 flex flex-wrap gap-2">
                {forms.map((f) => (
                  <button
                    key={f.slug}
                    onClick={() => setActiveForm(f)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeForm?.slug === f.slug
                        ? "bg-maroon text-white"
                        : "bg-cream-alt border border-gray-200 text-ink-soft hover:border-maroon hover:text-maroon"
                    }`}
                    data-testid={`form-tab-${f.slug}`}
                  >
                    {f.title}
                  </button>
                ))}
              </div>
              {activeForm && (
                <>
                  <p className="mt-5 text-sm text-ink-soft leading-relaxed">{activeForm.description}</p>
                  <div className="mt-6">
                    <DynamicForm key={activeForm.slug} form={activeForm} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
